
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  DialogActions,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import PageHeader from "../components/common/PageHeader";

import { useAuth } from "../context/AuthContext";
import { canCreateProject } from "../utils/roles";

/* Badge Colors */
const typeColors = {
  BAU: "default",
  DEVELOPMENT: "primary",
  CUSTOMIZATION: "secondary",
};

const statusColors = {
  ACTIVE: "success",
  COMPLETED: "default",
  ON_HOLD: "warning",
};

export default function Projects() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);

  // ⭐ ADDED: clients list
  const [clients, setClients] = useState([]);

  const [form, setForm] = useState({
    name: "",
    description: "",
    project_type: "DEVELOPMENT",
    client_id: "", // ⭐ ADDED
  });

  useEffect(() => {
    loadProjects();
	loadClients();   // ⭐ ADD THIS LINE for client lov
  }, []);

  const loadProjects = async () => {
    try {
      const res = await api.get("/projects/");
      setProjects(res.data);
    } catch (err) {
      console.error("Failed to load projects", err);
    }
  };

  /* =====================================
     ⭐ ADDED: LOAD CLIENTS FOR LOV
  ===================================== */

  const loadClients = async () => {
    try {
      const res = await api.get("/billing/clients");
      setClients(res.data || []);
    } catch (err) {
      console.error("Failed to load clients", err);
    }
  };

  const createProject = async () => {
    if (!canCreateProject(user?.role)) {
      alert("You are not authorized to create projects");
      return;
    }

    try {
      await api.post("/projects/", form);

      setOpen(false);

      // ⭐ UPDATED reset form
      setForm({
        name: "",
        description: "",
        project_type: "DEVELOPMENT",
        client_id: "",
      });

      loadProjects();
    } catch (err) {
      console.error("Project creation failed", err);
    }
  };

  const handleNavigate = (projectId, event) => {
    if (window.getSelection().toString()) return;
    navigate(`/projects/${projectId}`);
  };

  return (
    <Box className="page">
      <PageHeader title="Projects" />

      {canCreateProject(user?.role) && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
          >
            New Project
          </Button>
        </Box>
      )}

      {/* Project List */}
      <Grid container spacing={3}>
        {projects.map((project) => (
          <Grid item xs={12} md={6} lg={4} key={project.id}>

            <Card
              onClick={(e) => handleNavigate(project.id, e)}
              onKeyDown={(e) =>
                e.key === "Enter" && handleNavigate(project.id, e)
              }
              tabIndex={0}
              sx={{
                border: "1px solid #E2E8F0",
                cursor: "pointer",
                transition: "0.2s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 10px 20px rgba(0,0,0,0.08)",
                },
              }}
            >
              <CardContent>
                <Typography variant="h6" fontWeight="bold">
                  {project.name}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, mb: 2 }}
                >
                  {project.description || "No description"}
                </Typography>

                <Box display="flex" gap={1}>
                  <Chip
                    label={project.project_type}
                    color={typeColors[project.project_type] || "default"}
                    size="small"
                  />
                  <Chip
                    label={project.status}
                    color={statusColors[project.status] || "default"}
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>

          </Grid>
        ))}
      </Grid>

      {/* =====================================
         CREATE PROJECT DIALOG
      ===================================== */}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle>Create Project</DialogTitle>

        <DialogContent sx={{ mt: 1 }}>

          {/* ⭐ ADDED: CLIENT LOV */}

          <TextField
            select
            label="Client"
            fullWidth
            margin="normal"
            value={form.client_id}
            onChange={(e) =>
              setForm({ ...form, client_id: e.target.value })
            }
          >
            {clients.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Project Name"
            fullWidth
            margin="normal"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />

          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            margin="normal"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />

          <TextField
            select
            label="Project Type"
            fullWidth
            margin="normal"
            value={form.project_type}
            onChange={(e) =>
              setForm({ ...form, project_type: e.target.value })
            }
          >
            <MenuItem value="BAU">BAU</MenuItem>
            <MenuItem value="DEVELOPMENT">Development</MenuItem>
            <MenuItem value="CUSTOMIZATION">Customization</MenuItem>
          </TextField>

        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createProject}>
            Create
          </Button>
        </DialogActions>

      </Dialog>
    </Box>
  );
}